import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {Box, CircularProgress, Tab, Tabs, Typography} from '@mui/material'
import {useTheme} from '@mui/material/styles'
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined'
import moment from 'moment'
import Card from 'src/components-new/layout/Card'
import EmptyState from 'src/components-new/layout/EmptyState'
import DataTable from 'src/components-new/data/DataTable'
import {ColumnDef, PaginationState} from 'src/components-new/data/DataTable'
import Filters, {FilterItem, FilterResult} from 'src/components-new/data/Filters'
import AppDialog from 'src/components-new/feedback/AppDialog'
import FilePreviewDialog from 'src/components-new/feedback/FilePreviewDialog'
import InlineMessage from 'src/components-new/feedback/InlineMessage'
import ActionIconButton from 'src/components-new/primitives/ActionIconButton'
import Button from 'src/components-new/primitives/Button'
import StatusPill from 'src/components-new/primitives/StatusPill'
import {NEUTRAL_SCALE} from 'src/theme-new/tokens'
import {PATHS, STATUS} from 'src/constants/constants'
import {useAppSelector} from 'src/hooks'
import apiClient from 'src/services/apiClient'
import SecondaryAssumptionsPanel from 'src/pages-new/diary/SecondaryAssumptionsPanel'

interface DiarioTabProps {
  patientId: string
  serviceConf?: string
  userCode?: string
  userFirstName?: string
  userLastName?: string
}

const ROWS_PER_PAGE = 10

const COLLECTIONS = {
  VITAL_SIGNS: 'vital-signs-collection',
  THERAPEUTIC: 'therapeutic-parameters-collection',
}

type SubTab = 'vital' | 'therapeutic'

interface DiaryRecord {
  _id: string
  plannedDate?: string
  [key: string]: any
}

function formatCellValue(value: any): string {
  if (value === null || value === undefined) return '—'
  if (typeof value === 'boolean') return value ? 'Sì' : 'No'
  if (typeof value === 'string') {
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) {
      const d = new Date(value)
      if (!isNaN(d.getTime())) return d.toLocaleDateString('it-IT')
    }
    return value
  }
  if (typeof value === 'number') return String(value)
  return JSON.stringify(value)
}

interface FeedbackConfigField {
  id: string
  label: string
}

// A column whose `fields` array is populated (e.g. "Tipo complesso" grouping
// "Betabloccante" / "Calcioantagonista" / "Disopiramide") is rendered as a spanning
// group header over one sub-column per field, mirroring the legacy TableCustom
// component's `fields` support (src/components/elements/TableCustom/TableCustom.tsx).
interface FeedbackConfigColumn extends FeedbackConfigField {
  fields?: FeedbackConfigField[]
}

function buildColumnsFromFeedbackConfig(feedbackCols: FeedbackConfigColumn[]): ColumnDef<DiaryRecord>[] {
  const allCols: FeedbackConfigColumn[] = [{id: 'plannedDate', label: 'Data'}, ...feedbackCols]
  return allCols.map(col => {
    if (col.fields && col.fields.length > 0) {
      return {
        key: col.id,
        header: col.label,
        render: () => null, // never called: subColumns supplies rendering per field
        subColumns: col.fields.map(field => ({
          key: `${col.id}-${field.id}`,
          header: field.label,
          render: (row: DiaryRecord) => (
            <Typography variant="body2">{formatCellValue(row[col.id]?.[field.id])}</Typography>
          ),
        })),
      }
    }
    return {
      key: col.id,
      header: col.label,
      render: (row: DiaryRecord) => (
        <Typography variant="body2">
          {col.id === 'plannedDate' && row.plannedDate
            ? new Date(row.plannedDate).toLocaleDateString('it-IT', {day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'})
            : formatCellValue(row[col.id])}
        </Typography>
      ),
    }
  })
}

const FILTER_CONFIG: FilterItem[] = [
  {id: 'date', label: 'Data', type: 'rangePicker'},
]

interface SubTabPanelProps {
  patientId: string
  collection: string
  activeFilters: FilterResult[]
}

function SubTabPanel({patientId, collection, activeFilters}: SubTabPanelProps): JSX.Element {
  const [records, setRecords] = useState<DiaryRecord[]>([])
  const [columns, setColumns] = useState<ColumnDef<DiaryRecord>[]>([])
  const [count, setCount] = useState<number>(0)
  const [page, setPage] = useState<number>(0)
  const [status, setStatus] = useState<string>(STATUS.PENDING)
  const [columnsLoaded, setColumnsLoaded] = useState(false)

  useEffect(() => {
    setPage(0)
  }, [activeFilters])

  useEffect(() => {
    setColumnsLoaded(false)
    apiClient.getFeedbackConfig(collection, () => {})
      .then((fetchedCols: any) => {
        const cols = Array.isArray(fetchedCols) && fetchedCols.length > 0
          ? buildColumnsFromFeedbackConfig(fetchedCols)
          : []
        setColumns(cols)
        setColumnsLoaded(true)
      })
      .catch(() => setColumnsLoaded(true))
  }, [collection])

  const fetchData = useCallback(() => {
    if (!columnsLoaded) return

    apiClient.getPatientsFeedbacksValues(collection, patientId, setStatus, {page, rowsPerPage: ROWS_PER_PAGE, order: []}, activeFilters)
      .then((data: any) => { if (Array.isArray(data)) setRecords(data) })
      .catch(() => setStatus(STATUS.REJECTED))

    apiClient.getPatientsFeedbacksValuesCount(collection, patientId, () => {}, activeFilters)
      .then((countResult: any) => { const n = Number(countResult); if (!isNaN(n)) setCount(n) })
      .catch(() => {})
  }, [patientId, collection, page, activeFilters, columnsLoaded])

  useEffect(() => { fetchData() }, [fetchData])

  if (status === STATUS.PENDING) {
    return (
      <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}>
        <CircularProgress />
      </Box>
    )
  }

  if (status === STATUS.REJECTED) {
    return (
      <Box sx={{py: 4, textAlign: 'center'}}>
        <Typography variant="body2" color="error">Impossibile caricare i dati.</Typography>
      </Box>
    )
  }

  if (columns.length === 0) {
    return (
      <Box sx={{py: 4, textAlign: 'center'}}>
        <Typography variant="body2" color="text.secondary">Nessuna configurazione colonne disponibile.</Typography>
      </Box>
    )
  }

  return (
    <DataTable<DiaryRecord>
      columns={columns}
      rows={records}
      keyField="_id"
      loading={false}
      emptyMessage="Nessun dato disponibile"
      totalElements={count}
      pagination={{page, rowsPerPage: ROWS_PER_PAGE}}
      onPaginationChange={(next) => setPage(next.page)}
    />
  )
}

interface FeedbacksParametersDiaryPanelProps {
  patientId: string
  showExport?: boolean
}

interface DiarySubTabConfig {
  show?: boolean
  label?: string
}

const DEFAULT_SUBTAB_LABELS: Record<SubTab, string> = {
  vital: 'Parametri Vitali',
  therapeutic: 'Parametri Terapeutici',
}

const SUBTAB_COLLECTIONS: Record<SubTab, string> = {
  vital: COLLECTIONS.VITAL_SIGNS,
  therapeutic: COLLECTIONS.THERAPEUTIC,
}

interface SubTabDef {
  key: SubTab
  collection: string
  label: string
}

function FeedbacksParametersDiaryPanel({patientId, showExport}: FeedbacksParametersDiaryPanelProps): JSX.Element {
  const {subTabs} = useAppSelector(
    state => state.pspConfigReducer.data.patientDetailConfiguration?.diaryConfigurations ?? {}
  ) as {subTabs?: {vitalParameters?: DiarySubTabConfig; therapeuticParameters?: DiarySubTabConfig}}

  const [activeTab, setActiveTab] = useState<SubTab>('vital')
  const [activeFilters, setActiveFilters] = useState<FilterResult[]>([])
  const [statusDownload, setStatusDownload] = useState<string>()
  const [isExporting, setIsExporting] = useState(false)

  // Same endpoint as the legacy FeedbacksParametersDiary export: the backend builds a single
  // Excel covering both vital and therapeutic parameters, scoped by the active date filter.
  const handleExport = async () => {
    if (isExporting) return
    setIsExporting(true)
    try {
      const response = await apiClient.downloadDiaryExcel(patientId, setStatusDownload, activeFilters)
      if (!response) return
      forceDownload(response.data, `Diario_${patientId}.xlsx`)
    } finally {
      setIsExporting(false)
    }
  }

  // Separate, lighter fetch (count only, no filters) from the ones SubTabPanel performs for the
  // visible table: this one only decides which sub-tabs structurally exist, so it runs once per
  // patient rather than re-running every time the user changes filters/pagination.
  const [dataCounts, setDataCounts] = useState<Partial<Record<SubTab, number>>>({})
  const [countsLoaded, setCountsLoaded] = useState(false)

  useEffect(() => {
    setCountsLoaded(false)
    Promise.all([
      apiClient.getPatientsFeedbacksValuesCount(COLLECTIONS.VITAL_SIGNS, patientId, () => {}, [])
        .catch(() => 0),
      apiClient.getPatientsFeedbacksValuesCount(COLLECTIONS.THERAPEUTIC, patientId, () => {}, [])
        .catch(() => 0),
    ]).then(([vitalCount, therapeuticCount]) => {
      setDataCounts({
        vital: Number(vitalCount) || 0,
        therapeutic: Number(therapeuticCount) || 0,
      })
      setCountsLoaded(true)
    })
  }, [patientId])

  // `show !== false` (not `Boolean(show)`) for backward compatibility with PSPs already live on
  // DIARY_FEEDBACKS_PARAMETERS that don't yet have `diaryConfigurations.subTabs` in config — same
  // convention used for sharedDocuments/personalDocuments in MyDocumentsNew.tsx. Final visibility
  // also requires the patient to actually have data in that collection (`hasData`), independent of
  // config, per the "hide the whole section when there's nothing to show" requirement below.
  const subTabDefs: (SubTabDef & {show: boolean})[] = useMemo(() => [
    {
      key: 'vital',
      collection: SUBTAB_COLLECTIONS.vital,
      label: subTabs?.vitalParameters?.label ?? DEFAULT_SUBTAB_LABELS.vital,
      show: subTabs?.vitalParameters?.show !== false && (dataCounts.vital ?? 0) > 0,
    },
    {
      key: 'therapeutic',
      collection: SUBTAB_COLLECTIONS.therapeutic,
      label: subTabs?.therapeuticParameters?.label ?? DEFAULT_SUBTAB_LABELS.therapeutic,
      show: subTabs?.therapeuticParameters?.show !== false && (dataCounts.therapeutic ?? 0) > 0,
    },
  ], [subTabs, dataCounts])

  const visibleSubTabs = useMemo(() => subTabDefs.filter(tab => tab.show), [subTabDefs])

  // If the active tab stops being valid (e.g. config changes, or it never had data to begin with),
  // realign to the first tab that is actually visible instead of rendering a dead selection.
  useEffect(() => {
    if (countsLoaded && visibleSubTabs.length > 0 && !visibleSubTabs.some(tab => tab.key === activeTab)) {
      setActiveTab(visibleSubTabs[0].key)
    }
  }, [countsLoaded, visibleSubTabs, activeTab])

  if (!countsLoaded) {
    return (
      <Box sx={{display: 'flex', justifyContent: 'center', py: 6}}>
        <CircularProgress />
      </Box>
    )
  }

  if (visibleSubTabs.length === 0) {
    return (
      <EmptyState
        title="Nessun dato disponibile"
        description="Non sono ancora presenti parametri registrati per questo paziente."
      />
    )
  }

  const currentTab = visibleSubTabs.some(tab => tab.key === activeTab) ? activeTab : visibleSubTabs[0].key
  const singleTab = visibleSubTabs.length === 1 ? visibleSubTabs[0] : undefined

  return (
    <Box>
      {statusDownload === STATUS.REJECTED && (
        <InlineMessage severity="error" onClose={() => setStatusDownload(undefined)} sx={{mb: 2}}>
          {'Errore durante l\'esportazione del diario.'}
        </InlineMessage>
      )}

      <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1}}>
        {singleTab ? (
          // With a single visible sub-tab the Tabs switcher (which would also act as the only
          // visual indicator of what's being shown) is dropped, but the label still needs to be
          // shown somewhere so the user knows whether they're looking at vital or therapeutic
          // parameters — same fix as MyDocumentsNew.tsx's single-tab case.
          <Typography variant="h3" sx={{fontSize: 16, fontWeight: 600, color: NEUTRAL_SCALE[900]}}>
            {singleTab.label}
          </Typography>
        ) : (
          <Tabs
            value={currentTab}
            onChange={(_e, val: SubTab) => setActiveTab(val)}
            sx={{
              '& .MuiTab-root': {fontSize: 13, textTransform: 'none', minHeight: 40},
              '& .MuiTabs-indicator': {height: 2},
            }}
          >
            {visibleSubTabs.map(tab => (
              <Tab key={tab.key} value={tab.key} label={tab.label} />
            ))}
          </Tabs>
        )}

        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
          <Filters config={FILTER_CONFIG} onSubmit={setActiveFilters} />
          {showExport && (
            <Button variant="primary" startIcon={<FileDownloadOutlinedIcon />} onClick={handleExport} loading={isExporting}>
              {'Esporta'}
            </Button>
          )}
        </Box>
      </Box>

      {currentTab === 'vital' && (
        <SubTabPanel patientId={patientId} collection={COLLECTIONS.VITAL_SIGNS} activeFilters={activeFilters} />
      )}
      {currentTab === 'therapeutic' && (
        <SubTabPanel patientId={patientId} collection={COLLECTIONS.THERAPEUTIC} activeFilters={activeFilters} />
      )}
    </Box>
  )
}

// --- DIARY_DRUGS / DIARY_MORISKY branch ------------------------------------
// Ported from legacy src/components/templates/PatientDetailsComponent/Diary/Diary.tsx.
// Used whenever the PSP's diaryConfigurations.diaryType is NOT 'DIARY_FEEDBACKS_PARAMETERS'
// (i.e. the default 'DIARY_DRUGS', or 'DIARY_MORISKY').

const ASSUMPTION_STATE_OPTIONS = [
  {value: 'COMPLETED', label: 'Effettuata'},
  {value: 'PLANNED', label: 'Pianificata'},
  {value: 'TO_BE_PLANNED', label: 'In pianificazione'},
  {value: 'CANCELLED', label: 'Non effettuata'},
]

// Same success/warning/error/default palette used for the patient state pill
// in src/pages-new/patients/PatientsListNew.tsx, applied here to the diary "state" column.
function getAssumptionStateColor(state: string | undefined): 'success' | 'warning' | 'error' | 'info' | 'default' {
  if (!state) return 'default'
  if (state === 'COMPLETED') return 'success'
  if (state === 'PLANNED') return 'info'
  if (state === 'TO_BE_PLANNED') return 'warning'
  if (state === 'CANCELLED') return 'error'
  return 'default'
}

function getAssumptionStateLabel(state: string | undefined): string {
  if (!state) return '—'
  return ASSUMPTION_STATE_OPTIONS.find(option => option.value === state)?.label ?? state
}

// Defined locally rather than imported from src/pages/home/Home — legacy imports are
// blocked under pages-new/ by the no-restricted-imports ESLint rule (no type-only exemption).
interface AdherenceType {
  pspAdherencePercentage?: number
  totalAdherencePercentage?: number
}

interface DiaryColumnConfig {
  id: string
  label?: string
  type?: string
  format?: string
  defaultValue?: string
  default?: string
  [key: string]: any
}

// Used for DIARY_DRUGS when the PSP config has no columns (missing or `[]`, e.g. saved by the
// configurator before it required at least one): without it the table renders cards with only
// the action icons. Same shape psp-backend serves for a fully configured drugs diary.
const DEFAULT_DRUGS_DIARY_COLUMNS: DiaryColumnConfig[] = [
  {id: 'date', label: 'Data', type: 'date', format: 'ddd DD MMMM YYYY'},
  {id: 'date', label: 'Ora', type: 'date', format: 'HH:mm'},
  {id: 'drug', label: 'Farmaci'},
  {id: 'state', label: 'Stato'},
]

// Mirrors legacy TableCustom's renderCellValue default-case priority
// (src/components/elements/TableCustom/TableCustom.tsx:387-394): a column's
// `format` only applies for `type === 'date'`, since PSP config can declare
// two columns sharing the same `id` (e.g. "Data"/"Ora" both read `date`) that
// only differ by `format`.
function formatColumnValue(col: DiaryColumnConfig, value: any): string {
  if (col.type === 'date' && col.format && value) {
    return moment(value).format(col.format)
  }
  if (value === null || value === undefined) {
    return col.defaultValue ?? col.default ?? '—'
  }
  if (typeof value === 'boolean') return value ? 'Sì' : 'No'
  return String(value)
}

interface DrugsDiaryRow {
  _id: string
  date?: string
  drug?: string
  state?: string
  [key: string]: any
}

const INITIAL_VIEWER_STATE = {open: false, filePath: '', mime: '', fileName: '', status: STATUS.PENDING}

function buildAssumptionDownloadName(row: DrugsDiaryRow, userCode?: string): string {
  const dateStr = row.date
    ? new Date(row.date).toLocaleDateString('it-IT', {day: '2-digit', month: '2-digit', year: 'numeric'}).replace(/\//g, '-')
    : ''
  return `Scheda_infusionale_${userCode ?? ''}_${dateStr}.pdf`
}

function forceDownload(blob: Blob, downloadName: string): void {
  const url = (window.URL || window.webkitURL).createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = downloadName
  document.body.appendChild(anchor)
  anchor.click()
  document.body.removeChild(anchor)
  URL.revokeObjectURL(url)
}

interface DrugsDiaryPanelProps {
  patientId: string
  serviceConf?: string
  userCode?: string
  userFirstName?: string
  userLastName?: string
  diaryType?: string
  columns?: DiaryColumnConfig[]
  tableTitle?: string
  showExport?: boolean
  adherenceBox?: boolean
}

function DrugsDiaryPanel({
  patientId,
  serviceConf,
  userCode,
  userFirstName,
  userLastName,
  diaryType,
  columns = [],
  tableTitle = '',
  showExport,
  adherenceBox,
}: DrugsDiaryPanelProps): JSX.Element {
  const theme = useTheme()
  const [rows, setRows] = useState<DrugsDiaryRow[]>([])
  const [count, setCount] = useState<number>(0)
  const [status, setStatus] = useState<string>(STATUS.PENDING)
  const [statusCount, setStatusCount] = useState<string>(STATUS.PENDING)
  const [filters, setFilters] = useState<FilterResult[]>([])
  const [pagination, setPagination] = useState<PaginationState>({page: 0, rowsPerPage: 10, order: ''})

  const [adherence, setAdherence] = useState<AdherenceType>()
  const [statusAdherence, setStatusAdherence] = useState<string>()

  const [statusDownload, setStatusDownload] = useState<string>()
  const [viewerState, setViewerState] = useState(INITIAL_VIEWER_STATE)
  const [isExporting, setIsExporting] = useState(false)
  const [confirmExportOpen, setConfirmExportOpen] = useState(false)

  useEffect(() => {
    if (!adherenceBox) return
    apiClient.getPatientAdherence(patientId, setStatusAdherence)
      .then((fetchedAdherence: AdherenceType) => setAdherence(fetchedAdherence))
      .catch(() => setStatusAdherence(STATUS.REJECTED))
  }, [patientId, adherenceBox])

  // Scoped to patientId (via loadOptions) rather than the module-level FILTER_CONFIG
  // used by FeedbacksParametersDiaryPanel, since the "Terapia" options come from this
  // specific patient's therapies.
  const drugsFilterConfig: FilterItem[] = useMemo(() => [
    {id: 'date', label: 'Data', type: 'rangePicker'},
    {
      id: 'therapy',
      label: 'Terapia',
      type: 'select',
      loadOptions: async () => {
        const therapies = await apiClient.getPatientsTherapies(patientId, () => {})
        if (!Array.isArray(therapies)) return []
        // A patient can have several therapy records for the same drug (e.g. separate
        // courses over time) — dedupe by drugId so selecting "Voltaren" filters by all of them.
        const drugsById = new Map<string, string>()
        therapies.forEach((therapy: any) => {
          if (!therapy.drugId || !therapy.drug) return
          drugsById.set(therapy.drugId, therapy.drug)
        })
        return Array.from(drugsById.entries()).map(([drugId, drug]) => ({value: drugId, label: drug}))
      },
    },
  ], [patientId])

  const fetchData = useCallback(() => {
    if (diaryType === 'DIARY_MORISKY') {
      Promise.all([
        apiClient.getPatientDiaryMorisky(patientId, {...pagination}, setStatus, filters),
        apiClient.getPatientDiaryMoriskyCount(patientId, {}, setStatusCount, filters),
      ])
        .then(([data, countResult]) => {
          const list = Array.isArray(data) ? data : []
          setRows(list.map((item: any, i: number) => ({...item, _id: item._id ?? i})))
          const n = Number(countResult)
          if (!isNaN(n)) setCount(n)
        })
        .catch(() => setStatus(STATUS.REJECTED))
    } else {
      Promise.all([
        apiClient.getPatientsAssumptions(patientId, setStatus, pagination, filters),
        apiClient.getPatientsAssumptionsCount(patientId, setStatusCount, filters),
      ])
        .then(([data, countResult]) => {
          const list = Array.isArray(data) ? data : []
          setRows(list.map((assumption: any, i: number) => ({
            ...assumption,
            _id: assumption._id ?? i,
          })))
          const n = Number(countResult)
          if (!isNaN(n)) setCount(n)
        })
        .catch(() => setStatus(STATUS.REJECTED))
    }
  }, [patientId, diaryType, pagination, filters])

  useEffect(() => { fetchData() }, [fetchData])

  useEffect(() => {
    if (statusDownload === STATUS.REJECTED) setViewerState(INITIAL_VIEWER_STATE)
  }, [statusDownload])

  const handleSubmitFilters = (newFilters: FilterResult[]) => {
    setFilters(newFilters)
    setPagination(prev => ({...prev, page: 0}))
  }

  const handlePreviewAssumption = async (row: DrugsDiaryRow) => {
    const response = await apiClient.exportPatientAssumption(patientId, row._id, setStatusDownload)
    if (!response) return
    const downloadName = buildAssumptionDownloadName(row, userCode)
    const blob = new Blob([response.data], {type: 'application/pdf'})
    const url = (window.URL || window.webkitURL).createObjectURL(blob)
    setViewerState({open: true, filePath: url, mime: 'application/pdf', fileName: downloadName, status: STATUS.FULFILLED})
  }

  const handleDownloadAssumption = async (row: DrugsDiaryRow) => {
    const response = await apiClient.exportPatientAssumption(patientId, row._id, setStatusDownload)
    if (!response) return
    const downloadName = buildAssumptionDownloadName(row, userCode)
    const blob = new Blob([response.data], {type: 'application/pdf'})
    forceDownload(blob, downloadName)
  }

  const runExport = async () => {
    if (isExporting) return
    setIsExporting(true)
    try {
      if (diaryType === 'DIARY_DRUGS') {
        const response = await apiClient.downloadDiaryExcel(patientId, setStatusDownload, filters)
        if (!response) return
        const url = window.URL.createObjectURL(response.data)
        const a = document.createElement('a')
        a.href = url
        a.download = `Diario_${patientId}.xlsx`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)
      } else {
        const fileResponse = await apiClient.generatePdf(`${new URL(window.location.href).origin}/${PATHS.PDF_DIARY}?filters=${JSON.stringify({patientId})}`, setStatusDownload)
        if (!fileResponse) return
        const blob = new Blob([new Uint8Array(fileResponse)], {type: 'application/pdf'})
        const url = (window.URL || window.webkitURL).createObjectURL(blob)
        setViewerState({open: true, filePath: url, mime: 'application/pdf', fileName: `Diario - ${userFirstName} ${userLastName}`, status: STATUS.FULFILLED})
      }
    } finally {
      setIsExporting(false)
    }
  }

  // The 'date' filter (range picker in drugsFilterConfig above) is the only lever the user has to
  // shrink the export — without it, the export covers the patient's entire diary history, which
  // can be slow/heavy for patients with a long history. Nudge towards filtering first, but still
  // let them proceed unfiltered if that's really what they want.
  const hasDateFilter = filters.some(filter => filter.property === 'date')

  const handleExport = () => {
    if (!hasDateFilter) {
      setConfirmExportOpen(true)
      return
    }
    runExport()
  }

  const handleConfirmExport = () => {
    setConfirmExportOpen(false)
    runExport()
  }

  const configuredColumns = diaryType === 'DIARY_DRUGS' && columns.length === 0 ? DEFAULT_DRUGS_DIARY_COLUMNS : columns
  const filteredColumns = configuredColumns.filter((col) => col.id !== 'adherence' || serviceConf === 'EXTRA')

  const tableColumns: ColumnDef<DrugsDiaryRow>[] = [
    ...filteredColumns.map((col, idx) => ({
      key: `${col.id}-${idx}`,
      header: col.label ?? '',
      render: (row: DrugsDiaryRow) => col.id === 'state'
        ? <StatusPill label={getAssumptionStateLabel(row.state)} color={getAssumptionStateColor(row.state)} />
        : <Typography variant="body2">{formatColumnValue(col, row[col.id])}</Typography>,
    })),
    ...(diaryType === 'DIARY_DRUGS' ? [{
      key: 'actions',
      header: '',
      width: 56,
      isActionColumn: true,
      render: (row: DrugsDiaryRow) => (
        <Box onClick={(e: React.MouseEvent) => e.stopPropagation()} sx={{display: 'flex', gap: 0.5}}>
          <ActionIconButton variant="view" tooltip="Visualizza" onClick={() => handlePreviewAssumption(row)} />
          <ActionIconButton variant="download" tooltip="Scarica" onClick={() => handleDownloadAssumption(row)} />
        </Box>
      ),
    }] : []),
  ]

  return (
    <Box>
      {(status === STATUS.REJECTED || statusCount === STATUS.REJECTED || statusAdherence === STATUS.REJECTED) && (
        <InlineMessage
          severity="error"
          onClose={() => { setStatus(STATUS.FULFILLED); setStatusCount(STATUS.FULFILLED); setStatusAdherence(STATUS.FULFILLED) }}
          sx={{mb: 2}}
        >
          {'Errore nel caricare i dati.'}
        </InlineMessage>
      )}

      {statusDownload === STATUS.REJECTED && (
        <InlineMessage severity="error" onClose={() => setStatusDownload(undefined)} sx={{mb: 2}}>
          {'Errore nel caricare i dati.'}
        </InlineMessage>
      )}

      {adherenceBox && (
        <Box sx={{display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2}}>
          <Card sx={{flex: 1, minWidth: 200}}>
            <Typography variant="caption" sx={{fontWeight: 600, color: NEUTRAL_SCALE[700], textTransform: 'uppercase', letterSpacing: '0.05em'}}>
              {'Aderenza infusioni tutor'}
            </Typography>
            <Typography variant="h4" sx={{color: theme.palette.primary.main, fontWeight: 700}}>
              {adherence?.pspAdherencePercentage ? `${adherence.pspAdherencePercentage}%` : '-'}
            </Typography>
          </Card>
          <Card sx={{flex: 1, minWidth: 200}}>
            <Typography variant="caption" sx={{fontWeight: 600, color: NEUTRAL_SCALE[700], textTransform: 'uppercase', letterSpacing: '0.05em'}}>
              {'Aderenza complessiva'}
            </Typography>
            <Typography variant="h4" sx={{color: theme.palette.primary.main, fontWeight: 700}}>
              {adherence?.totalAdherencePercentage ? `${adherence.totalAdherencePercentage}%` : '-'}
            </Typography>
          </Card>
        </Box>
      )}

      <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1}}>
        <Typography variant="h3" sx={{fontSize: 16, fontWeight: 600, color: NEUTRAL_SCALE[900]}}>
          {tableTitle}
        </Typography>

        <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
          <Filters config={drugsFilterConfig} key="diary-filters" onSubmit={handleSubmitFilters} />
          {showExport && (
            <Button variant="primary" startIcon={<FileDownloadOutlinedIcon />} onClick={handleExport} loading={isExporting}>
              {'Esporta'}
            </Button>
          )}
        </Box>
      </Box>

      <DataTable<DrugsDiaryRow>
        columns={tableColumns}
        rows={rows}
        keyField="_id"
        loading={status === STATUS.PENDING || statusCount === STATUS.PENDING}
        emptyMessage="Nessun dato disponibile"
        totalElements={count}
        pagination={pagination}
        onPaginationChange={setPagination}
        mobileActionsPosition="bottom"
      />

      <FilePreviewDialog
        fileName={viewerState.fileName}
        filePath={viewerState.filePath}
        mime={viewerState.mime}
        onClose={() => setViewerState(INITIAL_VIEWER_STATE)}
        open={viewerState.open}
        status={viewerState.status}
      />

      <AppDialog
        actions={
          <>
            <Button variant="secondary" onClick={() => setConfirmExportOpen(false)}>
              {'Annulla'}
            </Button>
            <Button variant="primary" onClick={handleConfirmExport}>
              {'Continua'}
            </Button>
          </>
        }
        onClose={() => setConfirmExportOpen(false)}
        open={confirmExportOpen}
        title="Esporta diario senza filtri"
      >
        <Typography variant="body1">
          {'Non hai impostato un filtro per data: l\'export includerà tutto lo storico disponibile e potrebbe richiedere più tempo. Vuoi continuare?'}
        </Typography>
      </AppDialog>
    </Box>
  )
}

type DrugsSubTab = 'primary' | 'secondary'

interface DrugsDiarySubTabsProps extends DrugsDiaryPanelProps {
  subTabs?: {therapies?: DiarySubTabConfig; secondaryTherapies?: DiarySubTabConfig}
}

// DIARY_DRUGS only: "Terapie" (the DrugsDiaryPanel as it always was) and "Terapie Accessorie" (the
// patient's accessory therapies history, read-only for the doctor — same split as the patient's own
// diary, src/pages-new/diary/DiaryNew.tsx). Same rules as there: only an explicit `show: false`
// hides a tab (PSPs configured before the toggles existed have no keys and keep seeing everything),
// "Terapie Accessorie" only exists for a patient that actually has accessory therapies, and a lone
// visible tab is shown without the Tabs bar.
function DrugsDiarySubTabs({subTabs, ...panelProps}: DrugsDiarySubTabsProps): JSX.Element {
  const {patientId} = panelProps
  const showPrimaryTab = subTabs?.therapies?.show !== false
  const showSecondaryTab = subTabs?.secondaryTherapies?.show !== false
  const [secondaryTherapies, setSecondaryTherapies] = useState<Record<string, any>[]>([])
  // Distinct from `secondaryTherapies.length === 0`, which is also true until the fetch resolves:
  // only then is it known whether the "Terapie Accessorie" tab applies at all.
  const [secondaryTherapiesLoaded, setSecondaryTherapiesLoaded] = useState<boolean>(false)
  const [activeTab, setActiveTab] = useState<DrugsSubTab>('primary')

  useEffect(() => {
    setSecondaryTherapies([])
    if (!showSecondaryTab) {
      setSecondaryTherapiesLoaded(true)
      return
    }
    setSecondaryTherapiesLoaded(false)
    let cancelled = false
    // No-op status setter: a failure just leaves the tab absent, same as a patient without any
    // accessory therapy (and the "Terapie" tab keeps working on its own).
    apiClient.getPatientsSecondaryTherapies(patientId, () => {})
      .then((data: Record<string, any>[]) => { if (!cancelled) setSecondaryTherapies(Array.isArray(data) ? data : []) })
      .catch(() => {})
      .finally(() => { if (!cancelled) setSecondaryTherapiesLoaded(true) })
    return () => { cancelled = true }
  }, [patientId, showSecondaryTab])

  const hasSecondaryTherapies = showSecondaryTab && secondaryTherapies.length > 0
  const visibleTabs: DrugsSubTab[] = [
    ...(showPrimaryTab ? ['primary' as const] : []),
    ...(hasSecondaryTherapies ? ['secondary' as const] : []),
  ]

  if (visibleTabs.length === 0) {
    if (!secondaryTherapiesLoaded) {
      return <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}><CircularProgress size={24} /></Box>
    }
    return <EmptyState title="Nessun dato disponibile" description="Nessuna sezione del diario è attiva per questo paziente." />
  }

  const currentTab = visibleTabs.includes(activeTab) ? activeTab : visibleTabs[0]
  const labels: Record<DrugsSubTab, string> = {
    primary: subTabs?.therapies?.label ?? 'Terapie',
    secondary: subTabs?.secondaryTherapies?.label ?? 'Altre terapie',
  }

  return (
    <Box>
      {visibleTabs.length > 1 && (
        <Tabs
          value={currentTab}
          onChange={(_e, val: DrugsSubTab) => setActiveTab(val)}
          sx={{
            mb: 3,
            '& .MuiTab-root': {fontSize: 13, textTransform: 'none', minHeight: 40},
            '& .MuiTabs-indicator': {height: 2},
          }}
        >
          {visibleTabs.map(tab => <Tab key={tab} value={tab} label={labels[tab]} />)}
        </Tabs>
      )}

      {currentTab === 'primary' && <DrugsDiaryPanel {...panelProps} />}
      {currentTab === 'secondary' && (
        <>
          {visibleTabs.length === 1 && (
            // Lone tab: no Tabs bar to say what is being shown (the "Terapie" panel has its own
            // tableTitle), same as the single sub-tab of FeedbacksParametersDiaryPanel.
            <Typography variant="h3" sx={{fontSize: 16, fontWeight: 600, color: NEUTRAL_SCALE[900], mb: 2}}>
              {labels.secondary}
            </Typography>
          )}
          <SecondaryAssumptionsPanel secondaryTherapies={secondaryTherapies} readOnly />
        </>
      )}
    </Box>
  )
}

export default function DiarioTab({patientId, serviceConf, userCode, userFirstName, userLastName}: DiarioTabProps): JSX.Element {
  const {diaryType, columns, tableTitle, showExport, adherenceBox, subTabs} = useAppSelector(
    state => state.pspConfigReducer.data.patientDetailConfiguration?.diaryConfigurations ?? {}
  )

  if (diaryType === 'DIARY_FEEDBACKS_PARAMETERS') {
    return <FeedbacksParametersDiaryPanel patientId={patientId} showExport={showExport} />
  }

  const drugsPanelProps: DrugsDiaryPanelProps = {
    patientId,
    serviceConf,
    userCode,
    userFirstName,
    userLastName,
    diaryType,
    columns,
    tableTitle,
    showExport,
    adherenceBox,
  }

  // DIARY_MORISKY (and any other type) has no accessory therapies: it stays the plain panel.
  return diaryType === 'DIARY_DRUGS'
    ? <DrugsDiarySubTabs {...drugsPanelProps} subTabs={subTabs} />
    : <DrugsDiaryPanel {...drugsPanelProps} />
}
